import { type Bootstrap, type MissionActionName, type UiEvent, type UiMemoryRecord, type UiMissionView } from "./types";

/**
 * The client's ONLY door into VOSS. Every implementation talks to the Brain (via the host) and
 * never to the Intelligence Gateway. UI code depends on this interface, so tests inject a fake
 * and production injects the HTTP/SSE client — the components stay transport-agnostic.
 */
export interface VossClient {
  bootstrap(): Promise<Bootstrap>;
  createSession(workspaceId: string): Promise<string>;
  memory(workspaceId: string): Promise<readonly UiMemoryRecord[]>;
  converse(sessionId: string, message: string, options: { debug: boolean }, signal: AbortSignal): AsyncIterable<UiEvent>;
  getMission(missionId: string, workspaceId?: string): Promise<UiMissionView>;
  /** Drive a mission lifecycle action; the exact step/checkpoint id (when required) is passed in.
   * `workspaceId` is the trusted operating workspace (M49) the server checks against mission ownership. */
  missionAction(missionId: string, action: MissionActionName, payload?: { stepId?: string; checkpointId?: string; attemptId?: string; reason?: string }, workspaceId?: string): Promise<UiMissionView>;
  /** Accept a pending proposal (M47 gate 1) by its OPAQUE id → creates exactly one mission. */
  acceptProposal(proposalId: string, sessionId: string): Promise<UiMissionView>;
  /** Cancel a pending proposal by its opaque id. */
  rejectProposal(proposalId: string, sessionId: string): Promise<void>;
}

const jsonHeaders = { "content-type": "application/json" } as const;

/** Default client-side idle bound: ~2 missed server keepalives (server beats every ~15s). A silent
 *  stall longer than this means the connection died without a TCP RST (backgrounded tab, dropped
 *  mobile network, dead proxy) — we stop waiting and surface an honest, retryable timeout. */
const DEFAULT_IDLE_TIMEOUT_MS = 30_000;

/** Bound for the INITIAL response (REL-02): submit → first byte of the SSE response. A silent
 *  connection that dies before any response (dropped network, dead proxy) would otherwise leave
 *  `await fetch()` pending forever. 45s comfortably clears a local-model cold start (~6–9s observed);
 *  past it we surface an honest, retryable timeout rather than spin indefinitely. */
const DEFAULT_INITIAL_RESPONSE_TIMEOUT_MS = 45_000;

/**
 * Parse a Server-Sent-Events byte stream into UiEvents (one JSON object per `data:` frame).
 *
 * Reliability (REL-01): each `reader.read()` is raced against an idle timer. Without this, a silent
 * mid-stream stall leaves `read()` pending forever — the turn spins in the "Working" state with the
 * composer locked, escapable only by a page refresh that drops the in-memory conversation. On a stall
 * we cancel the reader and yield a terminal `error` (TIMEOUT, retryable) so the existing failure path
 * unlocks the UI and never presents a dead turn as still-in-progress. The timer RESETS on every read,
 * so a slow-but-progressing stream is never falsely killed.
 */
async function* parseSse(body: ReadableStream<Uint8Array>, signal: AbortSignal, idleTimeoutMs: number): AsyncIterable<UiEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let sawToken = false;
  let stalled = false;
  try {
    for (;;) {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const idle = new Promise<"idle">((resolve) => { timer = setTimeout(() => resolve("idle"), idleTimeoutMs); });
      const result = await Promise.race([reader.read(), idle]);
      clearTimeout(timer);
      if (result === "idle") {
        stalled = true;
        yield { type: "error", code: "TIMEOUT", retryable: true, partial: sawToken };
        return;
      }
      const { done, value } = result;
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const line = frame.split("\n").find((l) => l.startsWith("data:"));
        if (line) {
          const event = JSON.parse(line.slice(5).trim()) as UiEvent;
          if (event.type === "token") sawToken = true;
          yield event;
        }
        boundary = buffer.indexOf("\n\n");
      }
    }
  } finally {
    // On a stall/abort the read is still pending, so cancel the reader (which releases the stream);
    // on a clean end just release the lock, matching the prior behavior.
    if (stalled || signal.aborted) await reader.cancel().catch(() => undefined);
    else try { reader.releaseLock(); } catch { /* nothing pending on a clean end */ }
  }
}

/** Production client: streams turns from the VOSS host over fetch + SSE. `idleTimeoutMs` bounds a
 *  silent mid-stream stall (see REL-01); tests inject a short value. */
export function createHttpVossClient(baseUrl = "", opts: { idleTimeoutMs?: number; initialResponseTimeoutMs?: number } = {}): VossClient {
  const idleTimeoutMs = opts.idleTimeoutMs ?? DEFAULT_IDLE_TIMEOUT_MS;
  const initialResponseTimeoutMs = opts.initialResponseTimeoutMs ?? DEFAULT_INITIAL_RESPONSE_TIMEOUT_MS;
  return {
    async bootstrap() {
      const res = await fetch(`${baseUrl}/api/voss/bootstrap`);
      if (!res.ok) throw new Error(`bootstrap failed: ${res.status}`);
      return (await res.json()) as Bootstrap;
    },
    async createSession(workspaceId) {
      const res = await fetch(`${baseUrl}/api/voss/sessions`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ workspaceId }) });
      if (!res.ok) throw new Error(`createSession failed: ${res.status}`);
      return ((await res.json()) as { sessionId: string }).sessionId;
    },
    async memory(workspaceId) {
      const res = await fetch(`${baseUrl}/api/voss/memory?workspaceId=${encodeURIComponent(workspaceId)}`);
      if (!res.ok) throw new Error(`memory failed: ${res.status}`);
      return (await res.json()) as UiMemoryRecord[];
    },
    async *converse(sessionId, message, options, signal) {
      // REL-02: bound the INITIAL response so a connection that dies before the first byte can't hang
      // the turn forever. An internal controller (linked to the caller's signal) lets a timeout abort
      // the in-flight fetch; on timeout we surface an honest, retryable TIMEOUT — no final event, so
      // no assistant turn is created, no Origin panel is attached, and history is never mutated. Once
      // the response arrives, REL-01's per-read idle timeout takes over (parseSse).
      const ctrl = new AbortController();
      const onAbort = (): void => ctrl.abort();
      if (signal.aborted) ctrl.abort();
      else signal.addEventListener("abort", onAbort, { once: true });
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const timeout = new Promise<"timeout">((resolve) => { timer = setTimeout(() => resolve("timeout"), initialResponseTimeoutMs); });
        const raced = await Promise.race([
          fetch(`${baseUrl}/api/voss/converse`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ sessionId, message, debug: options.debug }), signal: ctrl.signal }),
          timeout,
        ]);
        clearTimeout(timer);
        if (raced === "timeout") {
          ctrl.abort(); // cancel the in-flight request; nothing was rendered, so partial is false
          yield { type: "error", code: "TIMEOUT", retryable: true, partial: false };
          return;
        }
        if (!raced.ok || !raced.body) throw new Error(`converse failed: ${raced.status}`);
        yield* parseSse(raced.body, ctrl.signal, idleTimeoutMs);
      } finally {
        clearTimeout(timer);
        signal.removeEventListener("abort", onAbort);
      }
    },
    async getMission(missionId, workspaceId) {
      const q = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : "";
      const res = await fetch(`${baseUrl}/api/voss/missions/${encodeURIComponent(missionId)}${q}`);
      if (!res.ok) throw new Error(`getMission failed: ${res.status}`);
      return (await res.json()) as UiMissionView;
    },
    async missionAction(missionId, action, payload, workspaceId) {
      const q = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : "";
      const res = await fetch(`${baseUrl}/api/voss/missions/${encodeURIComponent(missionId)}/${action}${q}`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify(payload ?? {}),
      });
      if (!res.ok) throw new Error(`mission ${action} failed: ${res.status}`);
      return (await res.json()) as UiMissionView;
    },
    async acceptProposal(proposalId, sessionId) {
      const res = await fetch(`${baseUrl}/api/voss/proposals/${encodeURIComponent(proposalId)}/accept`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({ sessionId }),
      });
      if (!res.ok) throw new Error(`accept proposal failed: ${res.status}`);
      return (await res.json()) as UiMissionView;
    },
    async rejectProposal(proposalId, sessionId) {
      const res = await fetch(`${baseUrl}/api/voss/proposals/${encodeURIComponent(proposalId)}/reject`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({ sessionId }),
      });
      if (!res.ok) throw new Error(`reject proposal failed: ${res.status}`);
    },
  };
}
